/**
 * NotificationBell — replaces the previous static bell icon in TopBar.
 * Fetches unread count on mount + every 60s, shows a red dot when > 0.
 * Clicking opens a dropdown that lists the most recent 10 notifications.
 * Clicking a row marks it read and navigates to the deeplink.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Check } from "lucide-react";
import { apiClient } from "../../services/api";

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - then) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const loadCount = useCallback(() => {
    apiClient
      .get("/notifications/unread-count")
      .then((r) => setCount(r.data?.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60_000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiClient
      .get("/notifications/inbox?limit=10")
      .then((r) => {
        const data = r.data;
        // Backend now returns { items, total, ... }; keep back-compat with the older array shape.
        setItems(Array.isArray(data) ? data : (data?.items || []));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openItem = async (it) => {
    setOpen(false);
    if (!it.read) {
      apiClient.post(`/notifications/inbox/${it.notification_id}/read`).catch(() => {});
      setCount((c) => Math.max(0, c - 1));
    }
    if (it.deeplink) navigate(it.deeplink);
  };

  const markAll = async () => {
    try {
      await apiClient.post("/notifications/mark-all-read");
      setCount(0);
      setItems((rows) => rows.map((r) => ({ ...r, read: true })));
    } catch { /* ignore */ }
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        data-testid="notifications-btn"
        onClick={() => setOpen((o) => !o)}
        className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/60 transition-colors"
        aria-label={count > 0 ? `${count} unread notifications` : "Notifications"}
        style={{ position: "relative", background: "transparent", border: 0 }}
      >
        <Bell size={20} strokeWidth={1.8} />
        {count > 0 && (
          <span
            data-testid="notif-unread-dot"
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 100,
              background: "#E5484D",
              color: "#fff",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: 9,
              display: "grid",
              placeItems: "center",
              border: "2px solid #F4F3F1",
            }}
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            data-testid="notif-dropdown"
            style={{
              position: "absolute",
              right: 0,
              top: "calc(100% + 6px)",
              width: 320,
              maxWidth: "92vw",
              maxHeight: 460,
              overflowY: "auto",
              background: "#FFFFFF",
              border: "1px solid #E7E5E1",
              borderRadius: 14,
              boxShadow: "0 12px 32px rgba(0,0,0,0.14)",
              padding: 6,
              zIndex: 60,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 10px 6px",
              }}
            >
              <div
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 10,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "#8A8884",
                  fontWeight: 700,
                }}
              >
                Notifications
              </div>
              {count > 0 && (
                <button
                  onClick={markAll}
                  data-testid="notif-mark-all"
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "#00B0D5",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    letterSpacing: "0.04em",
                  }}
                >
                  Mark all read
                </button>
              )}
            </div>

            {loading && (
              <div style={{ padding: 24, textAlign: "center", color: "#8A8884", fontSize: 12 }}>
                Loading…
              </div>
            )}

            {!loading && items.length === 0 && (
              <div style={{ padding: "24px 12px", textAlign: "center", color: "#8A8884", fontSize: 12 }}>
                You&apos;re all caught up.
              </div>
            )}

            {!loading &&
              items.map((it) => (
                <button
                  key={it.notification_id}
                  onClick={() => openItem(it)}
                  data-testid={`notif-item-${it.notification_id}`}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    display: "flex",
                    gap: 10,
                    padding: "10px 10px",
                    borderRadius: 10,
                    background: it.read ? "transparent" : "rgba(0,212,255,0.06)",
                    border: 0,
                    cursor: it.deeplink ? "pointer" : "default",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      marginTop: 6,
                      borderRadius: "50%",
                      background: it.read ? "transparent" : "#00D4FF",
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: 15,
                        color: "#1A1A1A",
                        lineHeight: 1.15,
                      }}
                    >
                      {it.title}
                    </div>
                    <div
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 12,
                        color: "#4A4845",
                        marginTop: 3,
                        lineHeight: 1.4,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                      }}
                    >
                      {it.message}
                    </div>
                    <div
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 10,
                        color: "#8A8884",
                        marginTop: 4,
                      }}
                    >
                      {fmtWhen(it.created_at)}
                    </div>
                  </div>
                  {it.read && <Check size={12} color="#B8B4AE" style={{ marginTop: 4, flexShrink: 0 }} />}
                </button>
              ))}

            {!loading && items.length > 0 && (
              <button
                onClick={() => { setOpen(false); navigate("/notifications"); }}
                data-testid="notif-see-all"
                style={{
                  width: "100%",
                  marginTop: 4,
                  padding: "10px",
                  borderRadius: 10,
                  background: "transparent",
                  border: 0,
                  borderTop: "1px solid #E7E5E1",
                  color: "#00B0D5",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: "0.06em",
                  cursor: "pointer",
                }}
              >
                See all notifications
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
